import { Button, Checkbox, Loading, Radio, Tooltip } from "@/components";
import { QuizDto, SubmitQuiz } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { isEmpty } from "lodash";
import { useEffect, useMemo } from "react";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useQuizDetail } from "../hooks/useQuizDetail";
import { useQuizSubmit } from "../hooks/useQuizSubmit";
import { submitQuizSchema } from "../schemas/quiz.schema";
import { useNavigate } from "react-router-dom";

interface Props {
  quiz: QuizDto;
  closeModal?: () => void;
}

// Option component for rendering individual answer options
const QuizOption = ({
  questionIndex,
  optionKey,
  content,
  isMultipleChoice,
}: {
  questionIndex: number;
  optionKey: string;
  content: string;
  isMultipleChoice: boolean;
}) => {
  const { control } = useFormContext<SubmitQuiz>();
  const fieldName = `answers.${questionIndex}.selected_options` as const;

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field }) => {
        const isSelected = isMultipleChoice
          ? (field.value || []).includes(optionKey)
          : (field.value || [])[0] === optionKey;

        const handleClick = () => {
          if (isMultipleChoice) {
            const currentValues = field.value || [];
            if (isSelected) {
              field.onChange(currentValues.filter((val) => val !== optionKey));
            } else {
              field.onChange([...currentValues, optionKey]);
            }
          } else {
            field.onChange([optionKey]);
          }
        };

        return (
          <div
            onClick={handleClick}
            className={`flex items-start gap-3 p-4 border ${
              isSelected ? "border-cyan" : "border-gray-200"
            } rounded-lg cursor-pointer hover:border-primary`}
          >
            <Tooltip side="bottom" content={`${optionKey}. ${content}`}>
              <div className="flex-1 text-sm truncate">
                <span className="mr-2">{optionKey}.</span>
                {content}
              </div>
            </Tooltip>
            {isMultipleChoice ? (
              <Checkbox
                id={`${questionIndex}-${optionKey}`}
                className="mt-0.5"
                checked={isSelected}
                onChange={() => {}} // Handled by parent div click
              />
            ) : (
              <Radio
                id={`${questionIndex}-${optionKey}`}
                className="mt-0.5"
                checked={isSelected}
                onChange={() => {}} // Handled by parent div click
              />
            )}
          </div>
        );
      }}
    />
  );
};

// Component to track answered questions
const AnsweredTracker = () => {
  const { control } = useFormContext<SubmitQuiz>();
  const answers = useWatch({
    control,
    name: "answers",
  });

  const answeredCount = useMemo(() => {
    if (!answers) return 0;
    return answers.filter(
      (answer) => (answer.selected_options?.length ?? 0) > 0
    ).length;
  }, [answers]);

  return (
    <div className="py-8 text-center text-4xl font-bold">
      {answeredCount}/{answers?.length || 0}
    </div>
  );
};

export const QuizDetail = ({ quiz, closeModal }: Props) => {
  const navigate = useNavigate();

  const { id } = quiz;

  const { data, isLoading } = useQuizDetail(id);

  const quizData = useMemo(() => {
    if (!isEmpty(data?.data)) {
      return data?.data.quizz;
    }
    return null;
  }, [data]);

  const questions = useMemo(() => {
    if (!isEmpty(data?.data)) {
      return data?.data.data || [];
    }
    return [];
  }, [data]);

  const form = useForm<SubmitQuiz>({
    resolver: zodResolver(submitQuizSchema),
    defaultValues: {
      quiz_id: id,
      answers: [],
    },
  });

  const { mutate, isPending } = useQuizSubmit();

  // Update form values when quiz data is loaded
  useEffect(() => {
    if (questions) {
      form.setValue(
        "answers",
        questions.map((q) => ({
          question_id: q.id ?? 0,
          selected_options: [],
        }))
      );
    }
  }, [quizData, form]);

  const onSubmit = (data: SubmitQuiz) => {
    mutate(data, {
      onSuccess: (result) => {
        closeModal?.();
        if (result.data?.user_quiz_id) {
          navigate(`/e-learning/quizzes/${result.data.user_quiz_id}/result`, {
            replace: true,
          });
        }
      },
    });
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex min-h-full gap-4"
      >
        {/* left side */}
        <div className="flex-1 bg-white h-full rounded-lg shadow-shadow_light_card overflow-y-auto">
          <span className="flex flex-row items-center justify-between px-4 py-2.5 border-b border-gray-300 sticky top-0 bg-white z-10">
            <Tooltip side="bottom" content={quizData?.title || ""}>
              <p className="text-lg font-bold text-dark truncate max-w-full">
                {quizData?.title}
              </p>
            </Tooltip>
          </span>
          <div className="p-4 flex flex-col gap-8">
            {questions.map((question, index) => {
              // Check if question has multiple correct answers
              const isMultipleChoice = question.multi_result;

              return (
                <div key={question.id ?? index} className="flex flex-col gap-4">
                  <Tooltip
                    side="bottom"
                    content={`${index + 1}. ${question.question}`}
                  >
                    <div className="font-semibold text-base truncate">
                      {index + 1}. {question.question}
                    </div>
                  </Tooltip>
                  <div className="grid grid-cols-2 gap-3">
                    <QuizOption
                      questionIndex={index}
                      optionKey="A"
                      content={question.option_a}
                      isMultipleChoice={isMultipleChoice}
                    />
                    <QuizOption
                      questionIndex={index}
                      optionKey="B"
                      content={question.option_b}
                      isMultipleChoice={isMultipleChoice}
                    />
                    <QuizOption
                      questionIndex={index}
                      optionKey="C"
                      content={question.option_c}
                      isMultipleChoice={isMultipleChoice}
                    />
                    <QuizOption
                      questionIndex={index}
                      optionKey="D"
                      content={question.option_d}
                      isMultipleChoice={isMultipleChoice}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* right side */}
        <div className="w-[23%] flex flex-col gap-4 max-h-[calc(100vh-96px)] sticky top-0">
          <div className="bg-white rounded-lg shadow-shadow_light_card overflow-y-auto">
            <span className="flex flex-row items-center justify-between px-4 py-2.5 border-b border-gray-300 sticky top-0 bg-white z-10">
              <p className="text-lg font-bold text-dark">回答済み</p>
            </span>
            <AnsweredTracker />
          </div>
          <div className="bg-white rounded-lg shadow-shadow_light_card p-4 flex flex-col gap-2">
            <Button
              type="submit"
              buttonType="primary"
              disabled={isPending}
              isLoading={isPending}
            >
              提出
            </Button>
            <Button
              onClick={closeModal}
              type="button"
              buttonType="text"
              disabled={isPending}
            >
              キャンセル
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
